import { viewStateKey, type Runtime } from "@baton/core";
import type { MeetingCalendarSkeletonView } from "../../skeletons/Skeletons.tsx";
import { meetingsScope } from "../list/choice.ts";

/**
 * 018 FR-018-282 (2026-09-29, the owner's request for a calendar "which is neat and matches basically
 * what MS Teams shows us"), `contracts/screens.md` § 018 Meeting agent, Meetings calendar: the meetings
 * calendar's views. A wide surface offers Teams' four views, Day, Work week (Monday to Friday, the
 * default), Week and Month; a phone offers Day and Agenda (the default), a week's meetings as a list. A
 * view that the surface does not offer is read as the nearest one it does, so a link or a remembered
 * view from the other surface still opens. The URL's `calendar_view` wins, then the view the Team last
 * chose on this kind of surface in this browser. It stands apart from `model.ts`, the ranges, so the
 * route's loading state (`routes/fallbacks.tsx`) draws the skeleton of the view that will mount
 * without loading them.
 */
/** Below it the surface is a phone's, offering Day and Agenda. */
export const NARROW_QUERY = "(width < 768px)";

export const WIDE_VIEWS = ["day", "work_week", "week", "month"] as const;
export const NARROW_VIEWS = ["day", "agenda"] as const;
export const MEETING_CALENDAR_VIEWS = ["day", "work_week", "week", "month", "agenda"] as const;
export type MeetingCalendarView = (typeof MEETING_CALENDAR_VIEWS)[number];

export function isMeetingCalendarView(value: unknown): value is MeetingCalendarView {
  return (MEETING_CALENDAR_VIEWS as readonly unknown[]).includes(value);
}

/** The view a surface draws for a view it may not offer: a phone's Agenda for a week or a month, a wide surface's Work week for Agenda. */
export function fitted(view: MeetingCalendarView, narrow: boolean): MeetingCalendarView {
  if (narrow) return view === "day" ? "day" : "agenda";
  return view === "agenda" ? "work_week" : view;
}

/** The skeleton a view draws while it loads: the time grid over its days, the month's grid or the agenda. */
export function skeletonOf(view: MeetingCalendarView): MeetingCalendarSkeletonView {
  if (view === "month") return { grid: "month" };
  if (view === "agenda") return { grid: "agenda" };
  return { grid: "time", days: view === "day" ? 1 : view === "work_week" ? 5 : 7 };
}

export function chosenMeetingCalendarView(runtime: Runtime, ws: string, params: URLSearchParams, narrow: boolean): MeetingCalendarView {
  const stored = runtime.storage.get(viewStateKey, meetingsScope(ws));
  const asked = params.get("calendar_view");
  const remembered = narrow ? stored?.meeting_calendar_phone_view : stored?.meeting_calendar_view;
  return fitted(isMeetingCalendarView(asked) ? asked : (remembered ?? (narrow ? "agenda" : "work_week")), narrow);
}
